import type { TLShape } from "tldraw";
import { renderWithRetries } from "./renderWithRetries";

/** Keep surviving content in one export so underlays and overlays share a z-order. */
export async function renderContentWithRetries<R>(
  shapes: TLShape[],
  render: (shapes: TLShape[]) => Promise<R | null>,
  beforeRetry?: () => Promise<void>,
): Promise<{ value: R | null; failed: TLShape[] }> {
  const recovered = await renderWithRetries(shapes, render, beforeRetry);
  if (recovered.rendered.length <= 1) {
    return {
      value: recovered.rendered[0]?.value ?? null,
      failed: recovered.failed,
    };
  }

  const failed = new Set(recovered.failed);
  const survivors = shapes.filter((shape) => !failed.has(shape));
  await beforeRetry?.();
  try {
    const value = await render(survivors);
    if (value !== null) return { value, failed: recovered.failed };
  } catch {
    // Some failures depend on the combination, rather than an individual shape.
  }

  // Retain the old image-free fallback if the surviving shapes still cannot coexist.
  const nonImages = survivors.filter((shape) => shape.type !== "image");
  if (nonImages.length > 0 && nonImages.length < survivors.length) {
    await beforeRetry?.();
    try {
      return { value: await render(nonImages), failed: recovered.failed };
    } catch {
      // No coherent content export is available.
    }
  }
  return { value: null, failed: recovered.failed };
}
